import { Entry, EntryCollection } from 'contentful';
import hash from 'object-hash';
import IPresentation from '@app/types/presentations/presentation/IPresentation';
import IPresentationMaster from '@app/types/presentations/master/IPresentationMaster';
import ICustomSlide from '@/app/src/types/presentations/presentation/ICustomSlide';
import ICloudinaryAsset from '@/libraries/src/cloudinary/ICloudinaryAsset';
import ResourceNotFoundError from '@app/types/errors/ResourceNotFoundError';
import ContentfulService from '../base/ContentfulService';
import SlideType from '../../types/presentations/SlideType';
import IPresentationMetadata from '../../types/presentations/presentation/IPresentationMetadata';

export default class PresentationService extends ContentfulService {
	public master?: IPresentationMaster;

	/**
	 * Get existing presentations from contentful,
	 * supports providing an optional query for detailled seaches
	 * @param locale
	 * @param query?
	 */
	public async getAll(locale?: string, query?: Record<string, any>): Promise<IPresentation[]> {
		const effectiveQuery: Record<string, any> = { ...(query || {}), content_type: 'presentation', include: 1, limit: 1000 }; // eslint-disable-line

		const entries: EntryCollection<any> = await this.deliveryClient.getEntries(effectiveQuery);

		return Promise.all(entries.items.map(async (entry: Entry<any>): Promise<IPresentation> => this.parse(entry)));
	}

	/**
	 * Get an existing presentation from contentful
	 * @param id
	 */
	public async get(id: string): Promise<IPresentation> {
		const entries: EntryCollection<any> = await this.deliveryClient.getEntries({
			content_type: 'presentation', // eslint-disable-line
			include: 1,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'sys.id': id
		});

		if (entries.items.length === 0) {
			throw new ResourceNotFoundError(
				`Presentation entry with id '${id}' could not be retrieved as it does not exist.`
			);
		}

		const entry: Entry<any> = entries.items[0];

		return this.parse(entry);
	}

	public async update(presentation: IPresentation, presentationMaster?: IPresentationMaster): Promise<void> {
		const managementEnvironment: any = await this.getManagementEnvironment();

		try {
			let entry: Record<string, any> = await managementEnvironment.getEntry(presentation.id);

			entry.fields.internalTitle = {
				de: presentation.settings.title
			};
			entry.fields.ownerId = {
				de: presentation.ownerId
			};
			entry.fields.settings = {
				de: presentation.settings
			};
			entry.fields.metadata = {
				de: presentation.metadata
			};
			entry.fields.customSlides = {
				de: Object.keys(presentation.customSlidesById)
					.filter((customSlideId: string): boolean => !!customSlideId)
					.map(
						(customSlideId: string): Record<string, any> => ({
							sys: {
								linkType: 'Entry',
								id: customSlideId
							}
						})
					)
			};

			if (presentationMaster) {
				entry.fields.presentationMaster = {
					de: {
						sys: {
							linkType: 'Entry',
							id: presentationMaster.id
						}
					}
				};

				entry.fields.presentationMasterChecksum = {
					de: presentationMaster.checksum
				};
			}

			entry = await entry.update();
			return entry.publish();
		} catch (error) {
			this.throwIfResourceNotFoundError(
				error,
				`Presentation entry with id '${presentation.id}' could not be updated as it does not exist.`
			);

			throw error;
		}
	}

	public async create(presentation: IPresentation, presentationMaster: IPresentationMaster): Promise<IPresentation> {
		if (!presentationMaster.snapshotId) throw new Error('A Cannot create a presentation without a master snapshot');

		const managementEnvironment: any = await this.getManagementEnvironment();
		const entryFields: Record<string, any> = {
			fields: {
				ownerId: {
					de: presentation.ownerId
				},
				settings: {
					de: presentation.settings
				},
				metadata: {
					de: presentation.metadata
				},
				internalTitle: {
					de: presentation.settings.title
				},
				presentationMaster: {
					de: {
						sys: {
							linkType: 'Entry',
							id: presentationMaster.id
						}
					}
				},
				presentationMasterChecksum: {
					de: presentationMaster.checksum
				},
				customSlides: {
					de: Object.values(presentation.customSlidesById)
						.filter((customSlide: ICustomSlide): boolean => !!customSlide.id)
						.map(
							(customSlide: ICustomSlide): Record<string, any> => ({
								sys: {
									linkType: 'Entry',
									id: customSlide.id
								}
							})
						)
				}
			}
		};

		let entry: Record<string, any> = await managementEnvironment.createEntry('presentation', entryFields);

		entry = await entry.publish();

		return this.get(entry.sys.id);
	}

	public async delete(id: string): Promise<void> {
		const managementEnvironment: any = await this.getManagementEnvironment();

		try {
			const entry: Record<string, any> = await managementEnvironment.getEntry(id);

			// unpublish and delete custom slides first if there are anay to not leave them orphaned
			if (entry.fields.customSlides) {

				const customSlides: Record<string, any>[] = Object.values(entry.fields.customSlides).pop() as Record<string, any>[];

				await Promise.all(
					customSlides.map(
						async (customSlide: Record<string, any>): Promise<void> => {
							const customSlideEntry: Record<string, any> = await managementEnvironment.getEntry(customSlide.sys.id);
							await customSlideEntry.unpublish();
							await customSlideEntry.delete();
						}
					)
				);
			}

			await entry.unpublish();
			await entry.delete();
		} catch (error) {
			this.throwIfResourceNotFoundError(
				error,
				`Presentation entry with id '${id}' could not be deleted as it does not exist.`
			);

			throw error;
		}
	}

	/**
	 * Initialize presentation:
	 *
	 * - create chapter, section and slide maps
	 * - filter and sort slides in chapters and sections
	 * - filter and sort sections in chapters
	 * - create filtered and sorted chapterIds array
	 */
	private async parse(entry: Record<string, any>): Promise<IPresentation> {
		const {fields} = entry;
		const {settings} = fields;
		// fallback for backwards compatibility < release #4
		const metadata: IPresentationMetadata = fields.metadata || { selectedChapterTitles: [] };
		const masterId: string = fields.presentationMaster.sys.id;
		const customSlidesById: Record<string, ICustomSlide> = {};

		if (fields.customSlides) {
			fields.customSlides.forEach((customSlideEntry: Entry<any>): void => {
				// skip deleted, but still referenced custom slides
				if (!customSlideEntry || !customSlideEntry.fields) return;

				customSlidesById[customSlideEntry.sys.id] = {
					id: customSlideEntry.sys.id,
					presentationId: entry.sys.id,
					chapterId: (customSlideEntry.fields.chapter as Entry).sys.id,
					headline: customSlideEntry.fields.headline as string,
					copy: customSlideEntry.fields.copy as string,
					badgeText: customSlideEntry.fields.badgeText as string,
					asset: customSlideEntry.fields.asset as any as ICloudinaryAsset,
					fields: {},
					type: SlideType.CustomSlide
				};
			});
		}

		if (!settings.contactIds) {
			// Upgrade to the new contact structure: SALESAPP-1465
			settings.contactIds = settings.additionalContactIds || [];

			// TODO we will clean up this later as soon the new release is on the production system
			// delete settings.additionalContactIds;

			// The primary contact is kept to determine the author of the presentation
			if (settings.primaryContactId) {
				settings.contactIds.unshift(settings.primaryContactId);
			}
		}

		if (settings.contactIds && settings.additionalContactIds) {
			// TODO we will clean up this later as soon the new release is on the production system
			// delete settings.additionalContactIds;
		}

		const presentation: IPresentation = {
			id: entry.sys.id,
			masterId,
			masterChecksum: fields.presentationMasterChecksum,
			ownerId: entry.fields.ownerId || '', // fallback for old presentations without ownerId prop)
			settings,
			metadata,
			customSlidesById,
			updatedAt: entry.sys.updatedAt,
			checksum: ''
		};

		presentation.checksum = this.getPresentationChecksum(presentation);

		return presentation;
	}

	/**
	 * Builds a checksum over a given presentation
	 */
	public getPresentationChecksum(presentation: IPresentation): string {
		return hash(
			JSON.stringify({
				masterId: presentation.masterId,
				masterChecksum: presentation.masterChecksum,
				customSlideAssets: Object.values(presentation.customSlidesById)
					.map((customSlide: ICustomSlide): ICloudinaryAsset | undefined => customSlide.asset)
					.filter((asset: ICloudinaryAsset | undefined): boolean => !!asset)
			})
		).substr(0, 7);
	}
}
